/**
 * Expired tab — every expired in-contract probe that feeds the "Expired" KPI
 * (header count === rows, since the full unpaginated expired.lines backs the
 * table). Promoted from the former ExpiredCalibrationsPanel slide-over during the
 * #991 tab refactor: same DataTable + useExpiredLinesTable + CSV export, minus the
 * SlidePanel chrome.
 */
import { Download, FilterX } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { downloadCSV, toCSV, type CSVColumn } from '@/lib/csv';
import { DataTable } from '@/shared/components/data-table';
import { useExpiredLinesTable } from '../../hooks/useExpiredLinesTable';
import type { CalibrationPlanningResponse, ExpiredLine } from '../../types';

const CSV_COLUMNS: CSVColumn<ExpiredLine>[] = [
  { header: 'Account', value: (row) => row.account },
  { header: 'Site', value: (row) => row.siteName ?? '' },
  { header: 'Sensor', value: (row) => row.sensor ?? '' },
  { header: 'Channel', value: (row) => row.channel ?? '' },
  { header: 'Probe serial', value: (row) => row.probeSerial ?? '' },
  { header: 'Model', value: (row) => row.probeModel ?? '' },
  { header: 'Method', value: (row) => row.methodBucket },
  { header: 'Service mode', value: (row) => row.serviceMode ?? '' },
  { header: 'Cert #', value: (row) => row.certNumber ?? '' },
  { header: 'Due date', value: (row) => row.dueDate },
  { header: 'Days overdue', value: (row) => row.daysOverdue },
  { header: 'Region', value: (row) => row.region ?? '' },
  { header: 'Room', value: (row) => row.room ?? '' },
  { header: 'In maintenance', value: (row) => (row.maintenance ? 'Yes' : 'No') },
];

interface ExpiredTabProps {
  expired: CalibrationPlanningResponse['expired'];
}

export function ExpiredTab({ expired }: ExpiredTabProps) {
  const table = useExpiredLinesTable(expired.lines);
  const hasFilters = Object.values(table.columnFilters).some(Boolean);

  const handleExport = () => {
    const filename = `calibration-expired-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCSV(filename, toCSV(table.sortedLines, CSV_COLUMNS));
    toast.success(`Exported ${table.sortedLines.length} expired calibration${table.sortedLines.length === 1 ? '' : 's'}`);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">Expired calibrations ({expired.total})</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {expired.distinctOrgs} account{expired.distinctOrgs === 1 ? '' : 's'} affected
            {' · '}
            <Badge variant="gray" className="align-middle">
              {expired.inMaintenance} in maintenance
            </Badge>
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={table.clearFilters}>
              <FilterX className="mr-1 h-4 w-4" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-1 h-4 w-4" /> Export
          </Button>
        </div>
      </div>

      <DataTable
        items={table.sortedLines}
        columns={table.columns}
        ctx={undefined}
        columnFilters={table.columnFilters}
        sortKey={table.sortKey}
        sortDirection={table.sortDirection}
        onSort={table.handleSort}
        onSetColumnFilter={table.setColumnFilter}
        getRowKey={(row) => `${row.orgId ?? row.account}-${row.sensor ?? ''}-${row.probeSerial ?? ''}-${row.dueDate}`}
        emptyState="No expired calibrations match these filters."
      />
    </div>
  );
}
